import {
  scheduleAppointment,
  startVerification,
  updateMetric,
  isEligibilityTokenExpired,
  refreshEligibilityToken,
} from 'externalAPIs/insurance-eligibility';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import Analytics from 'utils/Analytics';
import {getEmailFromSurveyData} from 'utils/getEmailFromSurveyData';
import {INSURANCE_PROVIDERS} from '../survey/config';

export const formatAppointmentTime = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) {
    return '';
  }

  try {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    // Format date part: "Aug 24"
    const dateOptions: Intl.DateTimeFormatOptions = {month: 'short', day: 'numeric'};
    const datePart = startDate.toLocaleDateString('en-US', dateOptions);

    // Format time parts: "10:00a"
    const formatTime = (date: Date): string => {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'p' : 'a';
      const displayHours = hours % 12 || 12; // Convert to 12-hour format
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes}${period}`;
    };

    const startTimePart = formatTime(startDate);
    const endTimePart = formatTime(endDate);

    return `${datePart}, ${startTimePart}-${endTimePart}`;
  } catch (error) {
    console.error('Error formatting appointment time:', error);
    return '';
  }
};

export const formatAppointmentStartTime = (startTime: string): string => {
  if (!startTime) {
    return '';
  }

  try {
    const startDate = new Date(startTime);

    // Format date part: "Aug 24"
    const dateOptions: Intl.DateTimeFormatOptions = {month: 'long', day: 'numeric'};
    const datePart = startDate.toLocaleDateString('en-US', dateOptions).toUpperCase();

    // Format time parts: "10:00a"
    const formatTime = (date: Date): string => {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12; // Convert to 12-hour format
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes} ${period}`;
    };

    const startTimePart = formatTime(startDate);

    return `${datePart} at ${startTimePart}`;
  } catch (error) {
    console.error('Error formatting appointment time:', error);
    return '';
  }
};

export const getInsuranceProviderName = (providerValue: string | undefined): string => {
  const providerMap: Record<string, string> = {
    aetna: 'Aetna',
    blue_cross_blue_shield: 'Blue Cross Blue Shield',
    united_healthcare: 'United Healthcare',
    other: 'Other',
  };

  return providerValue ? providerMap[providerValue] || providerValue : '';
};

export const phoneNumberAsDigits = (phoneNumber: string): string => {
  return `${phoneNumber.replace(/\D/g, '').replace(/^1/, '')}`;
};

export function getPayerIdByValue(value?: string) {
  return value ? INSURANCE_PROVIDERS.find(p => p.value === value)?.payerId : undefined;
}

export const handleScheduleAppointment = async (): Promise<{success: boolean; appointment?: any; error?: string}> => {
  try {
    const surveyData = SurveyData.read();
    const startTime = surveyData?.appointment_start_time;
    const endTime = surveyData?.appointment_end_time;
    const providerId = surveyData?.appointment_provider_id;
    let token = surveyData?.eligibility_token ?? '';
    const firstName = surveyData?.first_name ?? '';
    const lastName = surveyData?.last_name ?? '';
    const email = getEmailFromSurveyData();
    const phoneNumber = surveyData?.phone ?? '';
    const state = surveyData?.insurance_state ?? '';
    const memberID = surveyData?.member_id ?? '';
    const insurance = surveyData?.insurance_coverage ?? '';
    const dob = surveyData?.dob ?? '';
    const gender = surveyData?.gender ?? '';
    const personId = surveyData?.person_id ?? '';

    if (!startTime || !endTime || !providerId) {
      const errorMsg = 'No appointment time selected. Please select an appointment time from the survey first.';
      alert(errorMsg);
      return {success: false, error: errorMsg};
    }

    // Validate that the dates are valid
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      const errorMsg = 'Invalid appointment time. Please select a different appointment time.';
      alert(errorMsg);
      console.error('Invalid date values:', {startTime, endTime});
      return {success: false, error: errorMsg};
    }

    // Proactively refresh eligibility token if expired
    let tokenRefreshed = false;
    if (isEligibilityTokenExpired(token)) {
      const freshToken = await refreshEligibilityToken();
      if (freshToken) {
        token = freshToken;
        tokenRefreshed = true;
      }
    }

    const referralCode = surveyData?.referral_code;

    const doSchedule = () =>
      scheduleAppointment(
        Number(personId),
        firstName,
        lastName,
        email,
        `+1${phoneNumberAsDigits(phoneNumber)}`,
        startDate.toISOString(),
        Number(providerId),
        state?.toUpperCase(),
        memberID,
        getPayerIdByValue(insurance),
        dob,
        gender?.toLowerCase(),
        'eligible',
        token,
        referralCode,
      );

    try {
      const appointment = await doSchedule();
      return {success: true, appointment};
    } catch (firstError) {
      // If we already refreshed the token, don't retry
      if (tokenRefreshed) {
        throw firstError;
      }

      // Retry once with a fresh token
      console.log('Appointment scheduling failed, refreshing eligibility token and retrying...');
      const freshToken = await refreshEligibilityToken();
      if (!freshToken) {
        throw firstError;
      }

      token = freshToken;
      const appointment = await doSchedule();
      return {success: true, appointment};
    }
  } catch (error) {
    return {success: false, error: (error as Error).message};
  }
};

export const handleStartVob = async (): Promise<{success: boolean; error?: string}> => {
  try {
    const surveyData = SurveyData.read();
    const token = surveyData?.eligibility_token ?? '';
    const firstName = surveyData?.first_name ?? '';
    const lastName = surveyData?.last_name ?? '';
    const email = getEmailFromSurveyData();
    const phoneNumber = surveyData?.phone ?? '';
    const state = surveyData?.insurance_state ?? '';
    const memberID = surveyData?.member_id ?? '';
    const insurance = surveyData?.insurance_coverage ?? '';
    const dob = surveyData?.dob ?? '';
    const personId = surveyData?.person_id ?? '';

    const verificationResponse = await startVerification(
      Number(personId),
      firstName,
      lastName,
      dob,
      state?.toUpperCase(),
      memberID,
      getPayerIdByValue(insurance) ?? '',
      `+1${phoneNumberAsDigits(phoneNumber)}`,
      email,
      token,
    );

    console.log('verificationResponse', verificationResponse);
    return {success: true, error: verificationResponse.error};
  } catch (error) {
    return {success: false, error: (error as Error).message};
  }
};

export const sendISI = async (personId: number, patientId: number, isiScore: number) => {
  const surveyData = SurveyData.read();
  const token = surveyData?.eligibility_token ?? '';
  const metricResponse = await updateMetric(
    personId,
    patientId,
    'Insomnia Severity Index',
    isiScore,
    new Date().toISOString(),
    token,
  );

  if (metricResponse.error) {
    Analytics.track('Error sending ISI', {reason: metricResponse.error});
  } else {
    Analytics.track('ISI sent successfully', {isiTotal: isiScore});
  }
};
